import type { CoverageBounds, CoverageMatrixPayload } from '@freedom/shared';

export interface CoverageOverlay {
  imageUrl: string;
  bounds: CoverageBounds;
}

interface RgbaColor { red: number; green: number; blue: number; alpha: number }

const colorStops = [
  { signalDbm: -85, color: { red: 224, green: 66, blue: 68, alpha: 135 } },
  { signalDbm: -75, color: { red: 245, green: 142, blue: 48, alpha: 155 } },
  { signalDbm: -65, color: { red: 239, green: 211, blue: 67, alpha: 170 } },
  { signalDbm: -55, color: { red: 151, green: 213, blue: 72, alpha: 180 } },
  { signalDbm: -45, color: { red: 56, green: 201, blue: 132, alpha: 190 } },
];

export function coverageSignalColor(signalDbm: number, noCoverageDbm = -127): RgbaColor {
  if (!Number.isFinite(signalDbm) || signalDbm <= noCoverageDbm + 0.01) return { red: 0, green: 0, blue: 0, alpha: 0 };
  if (signalDbm <= (colorStops[0]?.signalDbm ?? -85)) return colorStops[0]?.color ?? { red: 224, green: 66, blue: 68, alpha: 135 };
  if (signalDbm >= (colorStops[colorStops.length - 1]?.signalDbm ?? -45)) return colorStops[colorStops.length - 1]?.color ?? { red: 56, green: 201, blue: 132, alpha: 190 };
  for (let index = 1; index < colorStops.length; index += 1) {
    const right = colorStops[index];
    const left = colorStops[index - 1];
    if (!left || !right || signalDbm > right.signalDbm) continue;
    const fraction = (signalDbm - left.signalDbm) / (right.signalDbm - left.signalDbm);
    return {
      red: Math.round(left.color.red + (right.color.red - left.color.red) * fraction),
      green: Math.round(left.color.green + (right.color.green - left.color.green) * fraction),
      blue: Math.round(left.color.blue + (right.color.blue - left.color.blue) * fraction),
      alpha: Math.round(left.color.alpha + (right.color.alpha - left.color.alpha) * fraction),
    };
  }
  return { red: 0, green: 0, blue: 0, alpha: 0 };
}

function decodeFloat32(base64: string): Float32Array {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return new Float32Array(bytes.buffer);
}

export function renderCoverageOverlay(matrix: CoverageMatrixPayload): CoverageOverlay {
  const values = decodeFloat32(matrix.valuesBase64);
  if (values.length !== matrix.rows * matrix.cols) throw new Error('Некорректный размер матрицы покрытия');
  const canvas = document.createElement('canvas');
  canvas.width = matrix.cols;
  canvas.height = matrix.rows;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas недоступен');
  const image = context.createImageData(matrix.cols, matrix.rows);
  values.forEach((signalDbm, index) => {
    const color = coverageSignalColor(signalDbm, matrix.noCoverageDbm);
    const offset = index * 4;
    image.data[offset] = color.red;
    image.data[offset + 1] = color.green;
    image.data[offset + 2] = color.blue;
    image.data[offset + 3] = color.alpha;
  });
  context.putImageData(image, 0, 0);
  return { imageUrl: canvas.toDataURL('image/png'), bounds: matrix.bounds };
}

export function renderInterferenceOverlay(values: Float32Array, rows: number, cols: number, bounds: CoverageBounds): CoverageOverlay {
  if (values.length !== rows * cols) throw new Error('Некорректный размер матрицы помех');
  const canvas = document.createElement('canvas');
  canvas.width = cols;
  canvas.height = rows;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('Canvas недоступен');
  const image = context.createImageData(cols, rows);
  const stops = [
    { signal: -110, color: [44, 88, 156, 35] },
    { signal: -100, color: [91, 73, 180, 90] },
    { signal: -90, color: [178, 63, 164, 135] },
    { signal: -80, color: [229, 69, 91, 175] },
    { signal: -70, color: [255, 139, 70, 195] },
  ];
  values.forEach((value, index) => {
    const offset = index * 4;
    if (value <= -126.9) return;
    let left = stops[0];
    let right = stops[stops.length - 1];
    for (let stopIndex = 1; stopIndex < stops.length; stopIndex += 1) {
      const candidate = stops[stopIndex];
      if (candidate && value <= candidate.signal) { left = stops[stopIndex - 1]; right = candidate; break; }
    }
    if (!left || !right) return;
    const fraction = Math.max(0, Math.min(1, (value - left.signal) / Math.max(1, right.signal - left.signal)));
    for (let channel = 0; channel < 4; channel += 1) image.data[offset + channel] = Math.round((left.color[channel] ?? 0) + ((right.color[channel] ?? 0) - (left.color[channel] ?? 0)) * fraction);
  });
  context.putImageData(image, 0, 0);
  return { imageUrl: canvas.toDataURL('image/png'), bounds };
}
